import Image from 'next/image'
import { FC, useState, ReactElement } from 'react'
import { Wrapper as GoogleWrapper, Status } from '@googlemaps/react-wrapper'

import http from '@/services/http'
import LocationAutocomplete from '@/components/LocationAutocomplete'
import generics from '@/utils/generics'
import { useStoreActions } from '@/store'
import { PropertyReport } from '@/types/property'

interface Props {
  initial_address: string
  error_message?: string
  onSelection: (
    selected_address: string,
    property_report: PropertyReport
  ) => void
}

const PropertySearch: FC<Props> = props => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(props.error_message)
  const [selectedAddress, setSelectedAddress] = useState(
    props.initial_address || ''
  )
  const { onProductClickedTracking, onProductViewedTracking } = useStoreActions(
    actions => actions.tracking
  )

  const handleSelection = (address: string) => {
    // Reset Errors on selection
    setError(null)

    const parsedAddress = address.replace(', USA', '').replace('#', 'Unit ')

    setSelectedAddress(parsedAddress)
  }

  const submitSelection = async () => {
    if (!selectedAddress) {
      return setError('Please enter a valid address.')
    }

    // reset any SSR errors
    setError(null)

    //tracking submission click
    const productClickedPayload = generics.buildProductClickedPayload(
      'home valuation',
      'Get estimate',
      '',
      selectedAddress || ''
    )
    onProductClickedTracking(productClickedPayload)

    //estimate
    const property = await fetchHomeValuation(selectedAddress)

    if (property) {
      props.onSelection(selectedAddress, property)
    }
  }

  const fetchHomeValuation = async selectedAddress => {
    setLoading(true)

    const { data } = await http.get<PropertyReport | null>(
      `${process.env.NEXT_PUBLIC_HOME_VALUATION_API}/api/v2/valuations?address=${selectedAddress}`
    )

    if (!data) {
      setError(
        'We were unable to find a home valuation for this address. Please try again.'
      )
    }

    setLoading(false)

    //tracking address estimate
    const productViewedPayload = generics.buildProductViewedPayload(
      'home valuation',
      'Estimate result',
      '',
      data ? 'home valuation found' : 'no home valuation found',
      selectedAddress || ''
    )
    onProductViewedTracking(productViewedPayload)

    return data
  }

  return (
    <section className="bg-blue-dark py-16">
      <div className="wrapper gutter display-grid grid-cols-1 md:grid-cols-12 gap-6 md:align-items-center">
        <div className="md:col-span-6 md:pr-12">
          <h1 className="text-white">How much is my house worth?</h1>
          <p className="text-white">
            Tell us your address and get an instant home value estimate.
          </p>
          <div className="display-grid grid-cols-1 md:grid-cols-6 gap-4 md:align-items-flex-end">
            <div className="md:col-span-4">
              <div className="FormControl">
                <label
                  className="FormLabel FormLabel--rightAddon capitalize text-white"
                  htmlFor="street_address"
                >
                  Street address
                </label>
                <GoogleWrapper
                  apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
                  libraries={['places']}
                  render={render}
                >
                  <LocationAutocomplete
                    className="FormInput"
                    onSelection={handleSelection}
                    initialValue={selectedAddress}
                    data-testid="form-field"
                    id="street_address"
                  />
                </GoogleWrapper>
              </div>
            </div>
            <div className="md:col-span-2">
              <button
                onClick={submitSelection}
                className={`Button Button--primary max-w-full w-full ${
                  loading ? 'is-loading' : ''
                }`}
              >
                Get estimate
              </button>
            </div>
          </div>
          {!!error && <span className="FormElementHelp is-error">{error}</span>}
        </div>
        <div className="display-none md:display-block md:col-span-6 text-right">
          <Image
            src="https://a.storyblok.com/f/102997/600x420/9c66eeab90/valuation-hero-600w.png"
            width={600}
            height={420}
            loading="lazy"
            alt="Image of house"
          />
        </div>
      </div>
    </section>
  )
}

const render = (status: Status): ReactElement => {
  if (status === Status.FAILURE) return null
  // Can put a loading spinner here
  if (status === Status.LOADING) return null
}

export default PropertySearch
